/**
 * Legal document content for the sign-in screen (PLA-1091).
 *
 * The markdown files in docs/legal/ are the single source of truth, imported
 * raw at build time and parsed into typed blocks here so the modal renders
 * them without a markdown dependency. The parser covers exactly the subset
 * those documents use: one `#` title, `##` sections, `- ` bullets,
 * standalone `*note*` lines, `**bold**` / `*em*` inline, and HTML comments
 * (Legal-only annotations) which are stripped and never rendered.
 */
import aiRaw from '../../../docs/legal/ai-governance-policy.md?raw';
import privacyRaw from '../../../docs/legal/privacy-policy.md?raw';
import termsRaw from '../../../docs/legal/terms-of-service.md?raw';

export type LegalDocId = 'terms' | 'privacy' | 'ai';

export type LegalBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  /** Standalone italic line (e.g. the draft-status notice). */
  | { kind: 'note'; text: string }
  | { kind: 'list'; items: string[] };

export interface LegalDoc {
  id: LegalDocId;
  /** Short label used by the sign-in footer link. */
  linkLabel: string;
  /** Document title from the markdown `#` heading. */
  title: string;
  /** First standalone italic line, shown as the modal subtitle. */
  subtitle?: string;
  blocks: LegalBlock[];
}

function parseLegalMarkdown(raw: string): Pick<LegalDoc, 'title' | 'subtitle' | 'blocks'> {
  // Legal-only annotations live in HTML comments; they must never render.
  // Apply repeatedly to avoid incomplete multi-character sanitization where
  // a new comment marker can be formed after an earlier replacement.
  let stripped = raw;
  let previous: string;
  do {
    previous = stripped;
    stripped = stripped.replace(/<!--[\s\S]*?-->/g, '');
  } while (stripped !== previous);
  const lines = stripped.split(/\r?\n/);

  let title = '';
  let subtitle: string | undefined;
  const blocks: LegalBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list && list.length > 0) blocks.push({ kind: 'list', items: list });
    list = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }
    if (!title && line.startsWith('# ')) {
      title = line.slice(2).trim();
      continue;
    }
    if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push({ kind: 'heading', text: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith('- ')) {
      flushParagraph();
      if (!list) list = [];
      list.push(line.slice(2).trim());
      continue;
    }
    // Standalone *italic* line: the first is the document subtitle, the rest
    // are inline notices (e.g. draft status).
    if (/^\*[^*].*\*$/.test(line)) {
      flushParagraph();
      flushList();
      const text = line.slice(1, -1).trim();
      if (!subtitle && blocks.length === 0) subtitle = text;
      else blocks.push({ kind: 'note', text });
      continue;
    }
    if (list) {
      // Wrapped continuation of the previous bullet.
      list[list.length - 1] += ` ${line}`;
      continue;
    }
    paragraph.push(line);
  }
  flushParagraph();
  flushList();

  return { title, subtitle, blocks };
}

export const LEGAL_DOCS: LegalDoc[] = [
  { id: 'terms', linkLabel: 'End User License Agreement', ...parseLegalMarkdown(termsRaw) },
  { id: 'privacy', linkLabel: 'Privacy Policy', ...parseLegalMarkdown(privacyRaw) },
  { id: 'ai', linkLabel: 'AI Governance Policy', ...parseLegalMarkdown(aiRaw) },
];

export const getLegalDoc = (id: LegalDocId): LegalDoc | null =>
  LEGAL_DOCS.find((doc) => doc.id === id) ?? null;
